import { useEffect, useMemo } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useAnalytics } from '@wbk/analytics';
import { useGetTicketingEvent } from '@wbk/api/hooks';
import StickyInfo from '@/components/events/StickyInfo';
import MatchDetailBanner from '@/components/match/MatchDetailBanner';
import { getEventStatus } from '@/util/events';
import Expandable from '@/components/common/Expandable';
import DeflatedBallWrapper from '@/components/error/DeflatedBallWrapper';
import FootballSpinner from '@/components/ui/spinners/FootballSpinner';
import MobileStickyBuy from '../../../components/match/MobileStickyBuy';

const EventPage = () => {
  const { t } = useTranslation();
  const { viewItemEvent } = useAnalytics();

  const { locale, query } = useRouter();

  const isPrivate = query.private === 'true';

  const {
    data: event,
    isLoading,
    error,
  } = useGetTicketingEvent({
    slug: query.slug as string,
    lang: locale,
    private: isPrivate,
  });

  useEffect(() => {
    if (event) {
      viewItemEvent({
        product_id: event._id,
        title: event?.title || '',
        price: event?.event_start_price,
      });
    }
    // eslint-disable-next-line
  }, [event]);

  const withTimeSlots = !!event?.time_slots;

  const eventStatus: ReturnType<typeof getEventStatus> = useMemo(() => {
    // If its time slots event, we don't need to check for tickets
    // The check is done in the timeslot-capacity API
    if (withTimeSlots) {
      return {
        message: 'buy_tickets',
        disabled: false,
      };
    }

    if (event) {
      return getEventStatus(event, locale);
    }

    return {
      message: 'no_tickets_available',
      disabled: true,
    };
  }, [event, withTimeSlots, locale]);

  if (isLoading) {
    return (
      <div className='mx-auto my-20 max-w-lg space-y-4'>
        <FootballSpinner className='text-primary-dark mx-auto h-80 w-80' />
      </div>
    );
  }

  if (error || !event) {
    return (
      <DeflatedBallWrapper>
        <div className='mx-auto max-w-xl rounded-lg bg-secondary-400/80 px-6 py-10 shadow-md'>
          <p className='mb-2 text-center text-3xl text-error'>
            {error?.message || 'Something went wrong'}
          </p>
        </div>
      </DeflatedBallWrapper>
    );
  }

  return (
    <>
      <NextSeo
        title={event.seo?.title}
        description={event.seo?.description}
        additionalMetaTags={[{ name: 'keywords', content: event.seo.keywords }]}
        openGraph={{ images: [{ alt: event.seo.title, url: event.poster }] }}
      />
      {/* Banner */}
      <MatchDetailBanner match={event} status={eventStatus} />

      {/* Event Details */}
      <section className='container grid grid-cols-6 gap-8 p-4 pb-16'>
        <div className='col-span-full space-y-6 lg:col-span-4'>
          <Expandable
            title={t('event:about')}
            titleAs='h2'
            className='!rounded-b-none !bg-transparent'
            btnClassName='border-b py-2 border-white/20 hover:bg-white/5 px-2'
          >
            <div className='py-2'>
              <div
                data-testid='event-description'
                className='prose inline text-text'
                dangerouslySetInnerHTML={{ __html: event.description || '' }}
              />
            </div>
          </Expandable>
        </div>

        <aside className='col-span-full lg:col-span-2'>
          <StickyInfo event={event} />
        </aside>
      </section>

      <MobileStickyBuy match={event} status={eventStatus} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<LayoutProps> = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'ar', [
        'common',
        'event',
        'booking',
        'queue_common',
        'auth_common',
        'ui_common',
      ])),
    },
  };
};

export default EventPage;
